import React, { useEffect, useState, useCallback } from 'react';
import { Table, Input, Button, Breadcrumb, Grid, Card, Row, Col, Statistic, Avatar, Tag, Tooltip, Dropdown, message, Select } from 'antd';
import {
  EyeOutlined,
  RightOutlined,
  SearchOutlined,
  UserOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PhoneOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { chauffeursService, citiesService } from '../../common/services';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Fonction pour générer une couleur à partir du nom
const getAvatarColor = (name) => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1',
    '#13c2c2', '#fa541c', '#2f54eb', '#a0d911', '#f5222d'
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Status config
const STATUS_CONFIG = {
  online: { color: '#52c41a', label: 'En ligne', icon: <CheckCircleOutlined /> },
  offline: { color: '#ff4d4f', label: 'Hors ligne', icon: <CloseCircleOutlined /> },
  busy: { color: '#faad14', label: 'Occupé', icon: <CarOutlined /> },
};

const Chauffeurs = () => {
  const [chauffeurs, setChauffeurs] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
    busy: 0
  });
  const navigate = useNavigate();

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Calcul des statistiques
  const calculateStats = useCallback((data) => {
    const online = data.filter(c => c.status?.toLowerCase() === 'online').length;
    const offline = data.filter(c => c.status?.toLowerCase() === 'offline').length;
    const busy = data.filter(c => c.status?.toLowerCase() === 'busy').length;

    setStats({
      total: data.length,
      online,
      offline,
      busy
    });
  }, []);

  // Charger les villes au montage
  useEffect(() => {
    citiesService.getAll()
      .then((res) => {
        const citiesData = res?.data || [];
        // Filtrer uniquement les villes actives
        const activeCities = citiesData.filter(c => c.isActive);
        setCities(activeCities);
        // Sélectionner la ville par défaut
        const defaultCity = activeCities.find(c => c.isDefault);
        if (defaultCity) {
          setSelectedCity(defaultCity._id);
        } else if (activeCities.length > 0) {
          setSelectedCity(activeCities[0]._id);
        }
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des villes :", err);
      });
  }, []);

  // Charger les chauffeurs quand la ville change
  useEffect(() => {
    if (!selectedCity) return;

    setLoading(true);
    chauffeursService.getAllChauffeurs(selectedCity)
      .then((data) => {
        const chauffeursData = Array.isArray(data) ? data : [];
        setChauffeurs(chauffeursData);
        calculateStats(chauffeursData);
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des chauffeurs :", err);
        message.error('Erreur lors du chargement des chauffeurs');
      })
      .finally(() => setLoading(false));
  }, [selectedCity, calculateStats]);

  // Filtrage par champ de recherche
  const filteredChauffeurs = chauffeurs.filter((chauffeur) =>
    chauffeur.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chauffeur.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chauffeur.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chauffeur.plaque?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Export Excel
  const exportToExcel = () => {
    const hideLoading = message.loading('Export Excel en cours...', 0);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredChauffeurs.map(item => ({
        Nom: item.nom,
        Telephone: item.phone,
        Voiture: item.voiture,
        Plaque: item.plaque,
        Status: item.status,
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Chauffeurs");
      XLSX.writeFile(workbook, "chauffeurs_yandex.xlsx");
      message.success(`${filteredChauffeurs.length} chauffeurs exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export Excel');
    } finally {
      hideLoading();
    }
  };

  // Export CSV
  const exportToCSV = () => {
    const hideLoading = message.loading('Export CSV en cours...', 0);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredChauffeurs.map(item => ({
        Nom: item.nom,
        Telephone: item.phone,
        Voiture: item.voiture,
        Plaque: item.plaque,
        Status: item.status,
      })));
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'chauffeurs_yandex.csv');
      message.success(`${filteredChauffeurs.length} chauffeurs exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export CSV');
    } finally {
      hideLoading();
    }
  };

  // Export PDF
  const exportToPDF = () => {
    const hideLoading = message.loading('Export PDF en cours...', 0);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Liste des chauffeurs YANDEX", 14, 15);
      doc.setFontSize(10);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 22);

      autoTable(doc, {
        startY: 30,
        head: [['Nom', 'Téléphone', 'Voiture', 'Plaque', 'Status']],
        body: filteredChauffeurs.map(item => [
          item.nom,
          item.phone,
          item.voiture,
          item.plaque,
          item.status
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [19, 194, 194] },
      });

      doc.save("chauffeurs_yandex.pdf");
      message.success(`${filteredChauffeurs.length} chauffeurs exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export PDF');
    } finally {
      hideLoading();
    }
  };

  // Menu dropdown pour export
  const exportMenuItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      label: 'Exporter Excel (.xlsx)',
      onClick: exportToExcel
    },
    {
      key: 'csv',
      icon: <DownloadOutlined style={{ color: '#1890ff' }} />,
      label: 'Exporter CSV',
      onClick: exportToCSV
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Exporter PDF',
      onClick: exportToPDF
    }
  ];

  // Colonnes du tableau
  const columns = [
    {
      title: 'Chauffeur',
      key: 'chauffeur',
      width: isMobile ? 150 : 220,
      render: (_, record) => {
        const initials = record.nom
          ?.split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar
              size={36}
              style={{
                backgroundColor: getAvatarColor(record.nom),
                fontWeight: 'bold',
                fontSize: 14
              }}
            >
              {initials || <UserOutlined />}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500, color: '#262626' }}>
                {record.nom || '-'}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {record.phone || '-'}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Véhicule',
      key: 'vehicule',
      width: 160,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar size={28} style={{ backgroundColor: '#13c2c2' }} icon={<CarOutlined />} />
          <span style={{ color: '#595959' }}>{record.voiture || '-'}</span>
        </div>
      )
    },
    {
      title: 'Plaque',
      dataIndex: 'plaque',
      key: 'plaque',
      width: 130,
      render: (plaque) => (
        <Tag color="cyan" style={{ fontWeight: 500, fontSize: 12 }}>
          {plaque || '-'}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'En ligne', value: 'online' },
        { text: 'Hors ligne', value: 'offline' },
        { text: 'Occupé', value: 'busy' },
      ],
      onFilter: (value, record) => record.status?.toLowerCase() === value,
      render: (status) => {
        const statusKey = status?.toLowerCase() || 'offline';
        const config = STATUS_CONFIG[statusKey] || STATUS_CONFIG.offline;
        return (
          <Tag
            icon={config.icon}
            style={{
              backgroundColor: config.color,
              color: 'white',
              border: 'none',
              fontWeight: 500,
              padding: '4px 10px',
              borderRadius: 6
            }}
          >
            {config.label}
          </Tag>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Voir détails">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/dashboard/chauffeur/${record.id}`)}
            style={{ borderRadius: 6 }}
          />
        </Tooltip>
      ),
    },
  ];

  // Style des lignes alternées
  const rowClassName = (_, index) => {
    return index % 2 === 0 ? 'table-row-light' : 'table-row-dark';
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .table-row-light {
            background-color: #ffffff;
          }
          .table-row-dark {
            background-color: #e6fffb;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #b5f5ec !important;
          }
          .ant-table-thead > tr > th {
            background: #fafafa !important;
            color: #262626 !important;
            font-weight: 600 !important;
          }
          .stat-card-cyan {
            background: linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-green {
            background: linear-gradient(135deg, #52c41a 0%, #95de64 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-red {
            background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-cyan .ant-statistic-title,
          .stat-card-green .ant-statistic-title,
          .stat-card-red .ant-statistic-title,
          .stat-card-orange .ant-statistic-title {
            color: rgba(255,255,255,0.85) !important;
            font-size: 13px !important;
          }
          .stat-card-cyan .ant-statistic-content,
          .stat-card-green .ant-statistic-content,
          .stat-card-red .ant-statistic-content,
          .stat-card-orange .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#13c2c2' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#13c2c2' }}>Inscription</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Chauffeurs YANDEX</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-cyan" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><TeamOutlined style={{ marginRight: 6 }} />Total chauffeurs</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-green" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CheckCircleOutlined style={{ marginRight: 6 }} />En ligne</span>}
                value={stats.online}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-red" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CloseCircleOutlined style={{ marginRight: 6 }} />Hors ligne</span>}
                value={stats.offline}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CarOutlined style={{ marginRight: 6 }} />Occupés</span>}
                value={stats.busy}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Filtres */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Sélectionner une ville"
                value={selectedCity}
                onChange={setSelectedCity}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                suffixIcon={<GlobalOutlined style={{ color: '#13c2c2' }} />}
              >
                {cities.map(city => (
                  <Select.Option key={city._id} value={city._id}>
                    {city.name} {city.isDefault && '(Par défaut)'}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <Input
                prefix={<SearchOutlined style={{ color: '#13c2c2' }} />}
                placeholder="Rechercher par nom, téléphone, ID ou plaque..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={24} md={6} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size={controlSize}
                  style={{
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)',
                    border: 'none'
                  }}
                >
                  Exporter {!isMobile && `(${filteredChauffeurs.length})`}
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={filteredChauffeurs.map((chauffeur) => ({
              ...chauffeur,
              key: chauffeur._id || chauffeur.id,
            }))}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50, 100],
              showTotal: (t, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {t} chauffeurs
                </span>
              ),
              showLessItems: isMobile
            }}
            loading={isLoading}
            size={controlSize}
            scroll={{ x: 700 }}
            rowClassName={rowClassName}
          />
        </Card>
      </div>
    </div>
  );
};

export default Chauffeurs;
